import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@cloudscape-design/components/app-layout';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Link from '@cloudscape-design/components/link';
import Modal from '@cloudscape-design/components/modal';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Table from '@cloudscape-design/components/table';
import Select from '@cloudscape-design/components/select';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { listPipelines, getMetrics, submitFeedback, MetricsResponse, submitAnalysisRequest, getAnalysisRequests, updateRequestStatus, deleteAnalysisRequest, AnalysisRequest, CustomMetric, getCustomMetrics, submitCustomMetric, reviewCustomMetric, editCustomMetric, deleteCustomMetric, RecommendationResponse, RecommendationDisposition, getRecommendationResponses, submitRecommendationResponse, getPendingApprovals } from '../apis/metricsApi';
import { getCurrentUserAlias } from '../apis/midwayAuth';
import { MetricsDashboard } from '../components/MetricsDashboard';
import { PipelineSelector } from '../components/PipelineSelector';
import { OrgHierarchyView } from '../components/OrgHierarchyView';

// Only these aliases may mark analysis requests Done or delete them.
const REQUEST_ADMINS = ['samasamb'];

// Status filter options for the "All requests" modal.
const REQUEST_STATUS_OPTIONS = [
  { label: 'All statuses', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Done', value: 'done' },
];

// Approver slot cell: alias + colored status (or "not required" when empty).
const approverCell = (alias?: string, status?: string) => {
  if (!alias) return <StatusIndicator type="info">not required</StatusIndicator>;
  const type = status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'pending';
  return <StatusIndicator type={type}>{alias} · {status || 'pending'}</StatusIndicator>;
};

const MetricsRecommenderPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pipelines, setPipelines] = useState<string[]>([]);
  const [pipelinesLoading, setPipelinesLoading] = useState(true);
  const [selected, setSelected] = useState<string>(searchParams.get('pipeline') || '');
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestModalVisible, setRequestModalVisible] = useState(false);
  const [viewRequestsVisible, setViewRequestsVisible] = useState(false);
  const [requestPipeline, setRequestPipeline] = useState('');
  const [requestAlias, setRequestAlias] = useState('');
  const [requests, setRequests] = useState<AnalysisRequest[]>([]);
  const [requestStatusFilter, setRequestStatusFilter] = useState<'all' | 'pending' | 'done'>('all');
  const [requestActionError, setRequestActionError] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [customMetrics, setCustomMetrics] = useState<CustomMetric[]>([]);
  const [recResponses, setRecResponses] = useState<RecommendationResponse[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [pendingApprovals, setPendingApprovals] = useState<CustomMetric[]>([]);
  const [approvalsVisible, setApprovalsVisible] = useState(false);
  const [reviewingId, setReviewingId] = useState('');
  const [reviewAction, setReviewAction] = useState<'approved' | 'rejected' | ''>('');
  const [approvalsError, setApprovalsError] = useState('');

  useEffect(() => {
    getAnalysisRequests().then(setRequests);
    getCurrentUserAlias().then(setCurrentAlias);
    getPendingApprovals().then(setPendingApprovals);
  }, []);

  const handleReviewApproval = async (item: CustomMetric, status: 'approved' | 'rejected') => {
    setReviewingId(item.id);
    setReviewAction(status);
    setApprovalsError('');
    try {
      await reviewCustomMetric(item.pipeline, item.id, status, currentAlias);
      setPendingApprovals(await getPendingApprovals());
      if (item.pipeline === selected) setCustomMetrics(await getCustomMetrics(selected));
    } catch (e: any) {
      setApprovalsError(e?.message || 'Failed to review — you may not be an approver.');
    } finally {
      setReviewingId('');
      setReviewAction('');
    }
  };

  const handleSelect = (pipeline: string) => {
    setSelected(pipeline);
    if (pipeline) {
      setSearchParams({ pipeline });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    listPipelines().then(setPipelines).catch(e => setError(e.message)).finally(() => setPipelinesLoading(false));
  }, []);

  useEffect(() => {
    const pipelineFromUrl = searchParams.get('pipeline') || '';
    if (pipelineFromUrl !== selected) {
      setSelected(pipelineFromUrl);
      if (!pipelineFromUrl) setData(null);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!selected) { setCustomMetrics([]); setRecResponses([]); return; }
    let ignore = false; // guard against a slow earlier pipeline overwriting the newly selected one
    setLoading(true);
    setError(null);
    getMetrics(selected).then(d => { if (!ignore) setData(d); }).catch(e => { if (!ignore) setError(e.message); }).finally(() => { if (!ignore) setLoading(false); });
    getCustomMetrics(selected).then(d => { if (!ignore) setCustomMetrics(d); });
    getRecommendationResponses(selected).then(d => { if (!ignore) setRecResponses(d); });
    return () => { ignore = true; };
  }, [selected]);

  const handleFeedback = async (metricName: string, helpful: string, reason?: string) => {
    if (!selected) return;
    try {
      await submitFeedback(selected, metricName, helpful, reason);
      setData(prev => prev ? {
        ...prev,
        feedback: { ...prev.feedback, [metricName]: helpful },
        feedbackReasons: { ...(prev.feedbackReasons || {}), [metricName]: helpful === 'not_helpful' ? (reason || '') : '' },
      } : prev);
    } catch (e: any) {
      setError(e?.message || 'Failed to save feedback. Please try again.');
    }
  };

  const handleSubmitCustomMetric = async (entry: { metricName: string; metricLink: string; justification: string; approver2: string }) => {
    if (!selected) return;
    await submitCustomMetric(selected, { ...entry, addedBy: currentAlias });
    // New submissions land in the central pending queue, not the pipeline folder.
    setPendingApprovals(await getPendingApprovals());
  };

  const handleReviewCustomMetric = async (id: string, status: 'approved' | 'rejected') => {
    if (!selected) return;
    await reviewCustomMetric(selected, id, status, currentAlias);
    setCustomMetrics(await getCustomMetrics(selected));
    setPendingApprovals(await getPendingApprovals());
  };

  const handleEditCustomMetric = async (id: string, entry: { metricName: string; metricLink: string; justification: string }) => {
    if (!selected) return;
    await editCustomMetric(selected, id, { ...entry, modifiedBy: currentAlias });
    setCustomMetrics(await getCustomMetrics(selected));
  };

  const handleDeleteCustomMetric = async (id: string) => {
    if (!selected) return;
    await deleteCustomMetric(selected, id, currentAlias);
    setCustomMetrics(await getCustomMetrics(selected));
  };

  const handleRespondToRecommendation = async (entry: { metricName: string; disposition: RecommendationDisposition; eta?: string; note?: string; reason?: string }) => {
    if (!selected) return;
    await submitRecommendationResponse(selected, { ...entry, respondedBy: currentAlias });
    setRecResponses(await getRecommendationResponses(selected));
  };

  return (
    <>
    <AppLayout
      navigationHide
      toolsHide
      contentType="default"
      content={
        <ContentLayout
          header={
            <Header
              variant="h1"
              description="Analyze pipelines and discover missing business metrics using AI-powered recommendations"
              actions={<Button onClick={() => navigate(-1)}>← Back</Button>}
            >
              <Link variant="primary" fontSize="heading-xl" onFollow={(e) => { e.preventDefault(); setSelected(''); setData(null); setSearchParams({}); }}>🔍 Business Metrics Recommender</Link>
            </Header>
          }
        >
          <SpaceBetween size="l">
            <PipelineSelector pipelines={pipelines} selected={selected} onSelect={handleSelect} loading={pipelinesLoading} onRequestClick={() => { setRequestAlias(currentAlias); setRequestPipeline(''); setRequestError(''); setRequestModalVisible(true); }} onViewRequestsClick={() => { getAnalysisRequests().then(setRequests); setRequestActionError(''); setViewRequestsVisible(true); }} onViewApprovalsClick={() => { getPendingApprovals().then(setPendingApprovals); setApprovalsVisible(true); }} pendingApprovalsCount={pendingApprovals.length} />
            {!selected && <OrgHierarchyView currentAlias={currentAlias} onSelectPipeline={handleSelect} />}
            {error && <Alert type="error" header="Something went wrong" dismissible onDismiss={() => setError(null)}>{error}</Alert>}
            {loading && (
              <Box textAlign="center" padding={{ vertical: 'xxl' }}>
                <SpaceBetween size="s" alignItems="center">
                  <Spinner size="large" />
                  <Box color="text-status-inactive">Loading metrics for {selected}...</Box>
                </SpaceBetween>
              </Box>
            )}
            {data && !loading && (data.metrics.length > 0 || data.autoRollbackAnalysis) && (
              <MetricsDashboard data={data} onFeedback={handleFeedback} customMetrics={customMetrics} currentAlias={currentAlias} onSubmitCustomMetric={handleSubmitCustomMetric} onReviewCustomMetric={handleReviewCustomMetric} onEditCustomMetric={handleEditCustomMetric} onDeleteCustomMetric={handleDeleteCustomMetric} recResponses={recResponses} onRespondToRecommendation={handleRespondToRecommendation} />
            )}
            {selected && data && !loading && data.metrics.length === 0 && !data.autoRollbackAnalysis && (
              <Box textAlign="center" padding={{ vertical: 'xxl' }} color="text-status-inactive">
                <SpaceBetween size="s" alignItems="center">
                  <Box variant="h3" color="inherit">{selected} hasn’t been analyzed yet</Box>
                  <Box variant="p" color="inherit">No business-metrics analysis exists for this pipeline yet. Request one and we’ll scan it.</Box>
                  <Button variant="primary" onClick={() => { setRequestPipeline(selected); setRequestAlias(currentAlias); setRequestModalVisible(true); }}>Request analysis for {selected}</Button>
                  <Button variant="link" onClick={() => { setSelected(''); setSearchParams({}); }}>← Back to org view</Button>
                </SpaceBetween>
              </Box>
            )}
          </SpaceBetween>
        </ContentLayout>
      }
    />
    <Modal
      visible={requestModalVisible}
      onDismiss={() => setRequestModalVisible(false)}
      header="Request Pipeline Analysis"
      footer={
        <SpaceBetween size="xs" direction="horizontal">
          <Button variant="link" onClick={() => setRequestModalVisible(false)}>Cancel</Button>
          <Button variant="primary" loading={requestSubmitting} onClick={async () => {
            setRequestError('');
            const errors = [];
            if (!requestPipeline.trim()) errors.push('Pipeline / Service name is required');
            if (!requestAlias.trim()) errors.push('Your alias is required');
            if (errors.length > 0) { setRequestError(errors.join('. ')); return; }
            setRequestSubmitting(true);
            try {
              await submitAnalysisRequest(requestPipeline.trim(), requestAlias.trim());
              setRequests(await getAnalysisRequests());
              setRequestPipeline('');
              setRequestAlias('');
              setRequestSuccess(true);
              setTimeout(() => { setRequestSuccess(false); setRequestModalVisible(false); }, 2000);
            } catch (e: any) {
              setRequestError(e?.message || 'Failed to submit the request. Please try again.');
            } finally {
              setRequestSubmitting(false);
            }
          }}>Submit Request</Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="m">
        {requestSuccess && <Alert type="success">Request submitted successfully! We'll analyze your pipeline soon.</Alert>}
        {requestError && <Alert type="error">{requestError}</Alert>}
        <FormField label="Pipeline / Service name" errorText={!requestPipeline.trim() && requestError ? 'Required' : ''}>
          <Input value={requestPipeline} onChange={({ detail }) => { setRequestPipeline(detail.value); setRequestError(''); }} placeholder="e.g., FlowRebinService" />
        </FormField>
        <FormField label="Your alias" description="Auto-filled from your login and can't be changed.">
          <Input value={requestAlias || currentAlias || '(unknown — reload if blank)'} disabled readOnly />
        </FormField>
      </SpaceBetween>
    </Modal>
    <Modal
      visible={viewRequestsVisible}
      onDismiss={() => { setRequestActionError(''); setViewRequestsVisible(false); }}
      header="All Pipeline Analysis Requests"
      size="large"
    >
      {(() => {
        const canManageRequests = REQUEST_ADMINS.includes(currentAlias);
        const denyAccess = (action: string) => setRequestActionError(`You don't have access to ${action} analysis requests. Only ${REQUEST_ADMINS.join(', ')} can do this.`);
        const filteredRequests = requests.filter((r) =>
          requestStatusFilter === 'all'
            ? true
            : requestStatusFilter === 'done'
              ? r.status === 'Done'
              : r.status !== 'Done',
        );
        const selectedStatusOption =
          REQUEST_STATUS_OPTIONS.find((o) => o.value === requestStatusFilter) || REQUEST_STATUS_OPTIONS[0];
        return (
          <SpaceBetween size="m">
            {requestActionError && <Alert type="error" dismissible onDismiss={() => setRequestActionError('')} header="Access denied">{requestActionError}</Alert>}
            <Box float="right">
              <Select
                selectedOption={selectedStatusOption}
                onChange={({ detail }) => setRequestStatusFilter((detail.selectedOption.value as 'all' | 'pending' | 'done'))}
                options={REQUEST_STATUS_OPTIONS}
                ariaLabel="Filter requests by status"
              />
            </Box>
            {/* Fixed-height scroll region so the list never grows past the viewport. */}
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              <Table
                items={filteredRequests}
                empty={<Box textAlign="center" color="text-status-inactive" padding="l">{requests.length === 0 ? 'No requests yet' : 'No requests match this filter'}</Box>}
                columnDefinitions={[
                  { id: 'pipeline', header: 'Pipeline', cell: (r: AnalysisRequest) => r.pipeline },
                  { id: 'requestedBy', header: 'Requested By', cell: (r: AnalysisRequest) => r.requestedBy },
                  { id: 'status', header: 'Status', cell: (r: AnalysisRequest) => <StatusIndicator type={r.status === 'Done' ? 'success' : 'pending'}>{r.status}</StatusIndicator> },
                  { id: 'date', header: 'Requested On', cell: (r: AnalysisRequest) => new Date(r.requestedAt).toLocaleString() },
                  { id: 'actions', header: '', cell: (r: AnalysisRequest) => (
                    <SpaceBetween size="xs" direction="horizontal">
                      {r.status !== 'Done' && <span style={{ cursor: 'pointer', fontSize: '18px' }} onClick={async () => { if (!canManageRequests) { denyAccess('mark done'); return; } try { await updateRequestStatus(r.pipeline, r.requestedAt, 'Done'); setRequests(await getAnalysisRequests()); } catch { /* surfaced elsewhere */ } }}>✅</span>}
                      <span style={{ cursor: 'pointer', fontSize: '18px' }} onClick={async () => { if (!canManageRequests) { denyAccess('delete'); return; } if (typeof window !== 'undefined' && !window.confirm(`Delete the request for ${r.pipeline}?`)) return; try { await deleteAnalysisRequest(r.pipeline, r.requestedAt); setRequests(await getAnalysisRequests()); } catch { /* surfaced elsewhere */ } }}>🗑️</span>
                    </SpaceBetween>
                  )},
                ]}
                sortingDisabled
              />
            </div>
          </SpaceBetween>
        );
      })()}
    </Modal>
    <Modal
      visible={approvalsVisible}
      onDismiss={() => setApprovalsVisible(false)}
      header={`Pending business-metric approvals (${pendingApprovals.length})`}
      size="large"
    >
      {pendingApprovals.length === 0 ? (
        <Box textAlign="center" color="text-status-inactive" padding="l">No pending approvals 🎉</Box>
      ) : (
        <>
          <Box padding={{ bottom: 's' }}><Alert type="info">Each metric needs both approvers. You can approve/reject items where you are listed as Approver 1 (pipeline owner) or Approver 2 (central team).</Alert></Box>
          {approvalsError && <Box padding={{ bottom: 's' }}><Alert type="error" dismissible onDismiss={() => setApprovalsError('')}>{approvalsError}</Alert></Box>}
          <Table
            items={pendingApprovals}
            columnDefinitions={[
              { id: 'pipeline', header: 'Pipeline', cell: (c: CustomMetric) => c.pipeline },
              { id: 'metric', header: 'Metric', cell: (c: CustomMetric) => c.metricLink ? <Link href={c.metricLink} external>{c.metricName}</Link> : c.metricName },
              { id: 'addedBy', header: 'Added by', cell: (c: CustomMetric) => c.addedBy },
              { id: 'approver1', header: 'Approver 1 (owner)', cell: (c: CustomMetric) => approverCell(c.approver1, c.approver1Status) },
              { id: 'approver2', header: 'Approver 2 (central)', cell: (c: CustomMetric) => approverCell(c.approver2, c.approver2Status) },
              { id: 'actions', header: '', cell: (c: CustomMetric) => {
                const canAct = (currentAlias === c.approver1 && c.approver1Status !== 'approved') || (currentAlias === c.approver2 && c.approver2Status !== 'approved');
                return canAct ? (
                  <SpaceBetween size="xs" direction="horizontal">
                    <Button variant="primary" disabled={reviewingId === c.id && reviewAction === 'rejected'} loading={reviewingId === c.id && reviewAction === 'approved'} onClick={() => handleReviewApproval(c, 'approved')}>Approve</Button>
                    <Button disabled={reviewingId === c.id && reviewAction === 'approved'} loading={reviewingId === c.id && reviewAction === 'rejected'} onClick={() => handleReviewApproval(c, 'rejected')}>Reject</Button>
                  </SpaceBetween>
                ) : <StatusIndicator type="pending">Awaiting approvers</StatusIndicator>;
              } },
            ]}
            sortingDisabled
          />
        </>
      )}
    </Modal>
    </>
  );
};

export default MetricsRecommenderPage;
